import React, { useMemo, useState } from 'react';
import { useTable } from 'react-table';
import { Card, Badge, Modal, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSyncAlt, 
    faEdit, 
    faInfoCircle, 
    faTimes,
    faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { SubscriptionService } from '../../Services/Axios/Api';
import { toast } from 'react-toastify';

const Subscriptions = ({ subscriptions, onUpdate }) => {
    const { t, i18n } = useTranslation();
    const [showRecurringModal, setShowRecurringModal] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);

    const columns = useMemo(
        () => [
            {
                Header: t('DonorProfile.Table.Campaign'),
                accessor: 'campaign',
                Cell: ({ value }) => <span className="campaign-name">{value.name}</span>,
            },
            {
                Header: t('DonorProfile.Table.Amount'),
                accessor: 'amount',
                Cell: ({ row }) => (
                    <Badge bg="success" className="donation-amount">
                        {row.original.currency} {row.original.amount.toLocaleString()}
                    </Badge>
                ),
            },
            {
                Header: t('DonorProfile.Table.StartDate'),
                accessor: 'startDate',
                Cell: ({ value }) => {
                    const date = new Date(value);
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    return (
                        <div className="donation-date">
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                            {`${day}/${month}/${year}`}
                        </div>
                    );
                },
            },
            {
                Header: t('DonorProfile.Table.NextBillingDate'),
                accessor: 'nextBillingDate',
                Cell: ({ value }) => {
                    const date = new Date(value);
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    return (
                        <div className="donation-date">
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                            {`${day}/${month}/${year}`}
                        </div>
                    );
                },
            },
            {
                Header: t('DonorProfile.Table.Status'),
                accessor: 'status',
                Cell: ({ value }) => (
                    <Badge bg={value === 'ACTIVE' ? 'success' : 'warning'} className="status-badge">
                        {value}
                    </Badge>
                ),
            },
            {
                Header: t('DonorProfile.Table.RecurringMode'),
                accessor: 'campaignRecurringMode',
                Cell: ({ row }) => (
                    <Badge bg="info" className="recurring-mode-badge">
                        {row.original.campaign.recurringMode === 'nextMonth' 
                            ? t('DonorProfile.Table.NextMonth') 
                            : t('DonorProfile.Table.CalendarMonth')}
                    </Badge>
                ),
            },
            {
                Header: t('DonorProfile.Table.Actions'),
                accessor: 'id',
                Cell: ({ row }) => (
                    row.original.status !== 'CANCELLED' && (
                        <Button
                            variant="link"
                            className="action-button"
                            onClick={() => handleRecurringClick(row.original)}
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </Button>
                    )
                ),
            },
        ],
        [t, i18n.language]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable({ columns, data: subscriptions });

    const handleRecurringClick = (subscription) => {
        setSelectedSubscription(subscription);
        setShowRecurringModal(true);
    };

    const handleToggleRecurring = async () => {
        if (!selectedSubscription) return;
        
        try {
            setIsUpdating(true);
            const newStatus = selectedSubscription.status.toUpperCase() === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
            if(newStatus === 'SUSPENDED'){
                await SubscriptionService.SuspendSubscription(selectedSubscription.id);
            }else{
                await SubscriptionService.ReactivateSubscription(selectedSubscription.id);
            }
            
            toast.success(t('DonorProfile.RecurringModal.StatusUpdated'));
            await onUpdate();
            setShowRecurringModal(false);
        } catch (error) {
            toast.error(t('DonorProfile.RecurringModal.UpdateFailed'));
            console.error('Error updating subscription status:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleCancelRecurring = async () => {
        if (!selectedSubscription) return;
        
        try {
            setIsUpdating(true);
            await SubscriptionService.CancelSubscription(selectedSubscription.id);
            
            toast.success(t('DonorProfile.RecurringModal.SubscriptionCancelled'));
            onUpdate();
            setShowRecurringModal(false);
        } catch (error) {
            toast.error(t('DonorProfile.RecurringModal.CancelFailed'));
            console.error('Error cancelling subscription:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <Card className="donor-card donations-card">
            <Card.Body>
                <div className="card-header-section d-flex justify-content-between align-items-center flex-wrap">
                    <h5 className="card-section-title mb-0 mb-2">
                        <FontAwesomeIcon icon={faSyncAlt} className="me-2" />
                        {t('DonorProfile.Subscriptions')}
                    </h5>
                    <div className="total-donations">
                        {t('DonorProfile.ShowingSubscriptions', { count: subscriptions.length })}
                    </div>
                </div>
                
                <div className="table-responsive">
                    <table className="donations-table" {...getTableProps()}>
                        <thead>
                            {headerGroups.map(headerGroup => {
                                const { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps();
                                return (
                                    <tr key={key} {...restHeaderGroupProps}>
                                        {headerGroup.headers.map(column => {
                                            const { key: columnKey, ...restHeaderProps } = column.getHeaderProps();
                                            return (
                                                <th 
                                                    key={columnKey}
                                                    {...restHeaderProps}
                                                >
                                                    {column.render('Header')}
                                                </th>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {rows.map(row => {
                                prepareRow(row);
                                const { key, ...restRowProps } = row.getRowProps();
                                return (
                                    <tr key={key} {...restRowProps}>
                                        {row.cells.map(cell => {
                                            const { key: cellKey, ...restCellProps } = cell.getCellProps();
                                            return (
                                                <td key={cellKey} {...restCellProps}>
                                                    {cell.render('Cell')}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Card.Body>

            {/* Recurring Management Modal */}
            <Modal 
                show={showRecurringModal} 
                onHide={() => setShowRecurringModal(false)}
                centered
                className="recurring-modal"
                dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faSyncAlt} className="me-2" /> {t('DonorProfile.RecurringModal.Title')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedSubscription && (
                        <div className="recurring-details">
                            <div className="campaign-info mb-4">
                                <h5>{selectedSubscription.campaign.name}</h5>
                                <div className="text-muted">
                                    <small>
                                        <FontAwesomeIcon icon={faCalendarAlt} className="me-2" /> 
                                        {t('DonorProfile.RecurringModal.StartedOn')} {(() => {
                                            const date = new Date(selectedSubscription.startDate);
                                            const day = String(date.getDate()).padStart(2, '0');
                                            const month = String(date.getMonth() + 1).padStart(2, '0');
                                            const year = date.getFullYear();
                                            return `${day}/${month}/${year}`;
                                        })()}
                                    </small>
                                </div>
                            </div>

                            <div className="recurring-status mb-4">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="mb-1">{t('DonorProfile.RecurringModal.SubscriptionStatus')}</h6>
                                        <p className="text-muted mb-0">
                                            {selectedSubscription.status === 'ACTIVE' 
                                                ? t('DonorProfile.RecurringModal.SubscriptionActive')
                                                : t('DonorProfile.RecurringModal.SubscriptionSuspended')}
                                        </p>
                                    </div>
                                    <Form.Check 
                                        type="switch"
                                        id="recurring-toggle"
                                        checked={selectedSubscription.status === 'ACTIVE'}
                                        onChange={handleToggleRecurring}
                                        disabled={isUpdating}
                                    />
                                </div>
                            </div>

                            <div className="recurring-mode-info mb-4">
                                <div className="info-box p-3 bg-light rounded">
                                    <h6 className="mb-2">
                                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" /> 
                                        {t('DonorProfile.RecurringModal.SubscriptionDetails')}
                                    </h6>
                                    <p className="mb-2">
                                        <strong>{t('DonorProfile.RecurringModal.Mode')}:</strong> 
                                        {selectedSubscription.campaign.recurringMode === 'nextMonth' 
                                            ? t('DonorProfile.Table.NextMonth') 
                                            : t('DonorProfile.Table.CalendarMonth')}
                                    </p>
                                    <p className="mb-2">
                                        <strong>{t('DonorProfile.RecurringModal.Amount')}:</strong> 
                                        {selectedSubscription.currency} {selectedSubscription.amount.toLocaleString()}
                                    </p>
                                    <p className="mb-0">
                                        <strong>{t('DonorProfile.RecurringModal.NextBilling')}:</strong> {(() => {
                                            const date = new Date(selectedSubscription.nextBillingDate);
                                            const day = String(date.getDate()).padStart(2, '0');
                                            const month = String(date.getMonth() + 1).padStart(2, '0');
                                            const year = date.getFullYear();
                                            return `${day}/${month}/${year}`;
                                        })()}
                                    </p>
                                </div>
                            </div>

                            <div className="recurring-actions">
                                <Button 
                                    variant="danger" 
                                    className="w-100 mb-2"
                                    onClick={handleCancelRecurring}
                                    disabled={isUpdating}
                                >
                                    <FontAwesomeIcon icon={faTimes} className="me-2" /> 
                                    {t('DonorProfile.RecurringModal.CancelSubscription')}
                                </Button>
                                <Button 
                                    variant="secondary" 
                                    className="w-100"
                                    onClick={() => setShowRecurringModal(false)}
                                >
                                    {t('DonorProfile.RecurringModal.Close')}
                                </Button>
                            </div>
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </Card>
    );
};

export default Subscriptions; 